import { RuleItemTypes, RuleOperators } from "../../constants/action-types";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DropdownLayout from "../../components/DropdownLayout";
import InputWithVariablePicker from "../components/InputWithVariablePicker";
import { getApiBaseUrl, isJiraContext } from "../../api/api";
import RuleFieldMapper from "./RuleFieldMapper";
import DatePickerField from "../../components/ui/DatePickerField";
import { fromJS } from "immutable";
import { filters, events, actions, isManagedByApp } from "./ruleItemDefinitions";
import RuleItemAppIcon from "./RuleItemAppIcon";
import {
  RuleItemEditorSlack,
  RuleItemEditorEmail,
  RuleItemEditorMailchimp,
  RuleItemEditorContact2Sale,
  RuleItemEditorAIField,
  RuleItemEditorFieldPicker,
} from "./editors";
import type { RuleItemEditorBaseProps } from "./ruleItemEditorTypes";

/** Step context variables for Set Field "to" value (previous step output). Match backend getActionContext. */
const STEP_CONTEXT_VARIABLES = [
  "previousStepFirstResult",
  "previousStep.output.aiFieldResults",
  "previousStep.output.newValue",
];

type RuleBuilderProps = {
  rule?: any;
  onChange: (value: any) => void;
  value?: any;
  itemType?: string;
  inputType?: string;
  placeholder?: string;
  error?: boolean;
  readonly?: boolean;
  /** When true, shows static label (no dropdown) but still renders sub-editor below. Used for configured actions. */
  lockedSelection?: boolean;
  isNewRule?: boolean;
};

function RuleItemBuilder({
  rule = fromJS({}),
  onChange,
  value = {
    label: "",
    value: "",
    data: {
      operator: "",
      fieldId: "",
    },
  },
  readonly = false,
  lockedSelection = false,
  isNewRule = false,
  itemType = RuleItemTypes.EVENT,
  inputType = "",
  placeholder,
}: RuleBuilderProps) {
  const { t } = useTranslation();
  const [ruleItem, setRuleItem] = useState<any>(value);
  const isLocalUpdateRef = useRef(false);
  const ruleItemRef = useRef(ruleItem);

  useEffect(() => {
    ruleItemRef.current = ruleItem;
  }, [ruleItem]);

  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));
  const ruleReducer = useSelector((state: any) => state.ruleReducer);
  const attributes = useSelector((state: any) => state.template.get("attributes") || fromJS([]));
  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const currentUserEmail = currentUser?.get("email");
  const editorBodyRef = useRef(null);
  const supportedFieldTypes = [
    "string",
    "bool",
    "boolean",
    "double",
    "guid",
    "datetime",
    "stringArray",
  ];

  const isFilterRule = itemType == RuleItemTypes.FILTER;

  const updateRuleItem = (newRuleItem: any) => {
    isLocalUpdateRef.current = true;
    setRuleItem(newRuleItem);
    onChange(newRuleItem);
  };

  useEffect(() => {
    if (isLocalUpdateRef.current) {
      isLocalUpdateRef.current = false;
      return;
    }
    if (value?.value !== ruleItem?.value) {
      setRuleItem(value);
    }
  }, [value?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ruleItem?.data?.type === "EMAIL" && !ruleItem?.data?.sender && currentUserEmail) {
      updateRuleItem({
        ...ruleItem,
        data: { ...ruleItem.data, sender: currentUserEmail },
      });
    }
  }, [ruleItem?.data?.type, currentUserEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSupportedFields = (item, data) => {
    // For SET_FIELD, filter by the action's inputType (entity the action runs on)
    const formType = data.operator === RuleOperators.SET_FIELD ? data.inputType : data.type;
    return item.get("formType") == formType && supportedFieldTypes.includes(item.get("type"));
  };
  const editorBaseProps: RuleItemEditorBaseProps = {
    ruleItem,
    updateRuleItem,
    rule,
    readonly,
    itemType,
    inputType,
    attributes,
    fields,
    ruleReducer,
    t,
  };

  const renderFieldPicker = (data: any) => {
    if (data.type === "SLACK") {
      return <RuleItemEditorSlack {...editorBaseProps} data={data} />;
    }
    if (data.type === "EMAIL") {
      return <RuleItemEditorEmail {...editorBaseProps} data={data} editorBodyRef={editorBodyRef} />;
    }
    if (data.type === "MAILCHIMP") {
      return <RuleItemEditorMailchimp {...editorBaseProps} data={data} />;
    }
    if (data.type === "CONTACT" && itemType === RuleItemTypes.ACTION) {
      return <RuleItemEditorContact2Sale {...editorBaseProps} data={data} />;
    }
    if (data.type === "AI_FIELD" && itemType === RuleItemTypes.ACTION) {
      return <RuleItemEditorAIField {...editorBaseProps} data={data} />;
    }
    if (data.fieldPicker && fields) {
      const availableFields = fields
        .filter((item: any) => isSupportedFields(item, data))
        .filter(
          (item: any) =>
            data.operator !== RuleOperators.SET_FIELD || item.get("isEditable") !== false
        )
        .map((item: any) => ({ label: item.get("label"), value: item.get("id") }))
        .toJS();
      return (
        <RuleItemEditorFieldPicker
          {...editorBaseProps}
          data={data}
          availableFields={availableFields}
          isFilterRule={isFilterRule}
        />
      );
    }
  };

  const renderFieldType = (data, fieldId, placeholder) => {
    const fieldValues = ruleReducer.get(data.fieldId);
    if (fieldValues) {
      const values = ruleReducer.get(data.fieldId).toJS();
      const isCustomDate = data[fieldId]?.key === "custom_date";

      // Check if selected field is a date type
      const selectedField = fields?.find((f: any) => f.get("id") === data.fieldId);
      const isDateField = selectedField && selectedField.get("type") === "datetime";

      const color = data[fieldId]?.color || "text-blue-400";
      const displayLabel = isCustomDate
        ? data[fieldId]?.value || t("custom date")
        : data[fieldId]?.label || "something";
      const activator = (
        <div className={"flex"}>
          <div className={"pr-2"}> {t(placeholder)}</div>
          <div className={`decoration-1 underline cursor-pointer ${color}`}>{displayLabel}</div>
        </div>
      );
      return (
        <div className={"align-middle pl-2"}>
          <DropdownLayout
            activator={activator}
            fullWidth={false}
            positionClassName={"left-0 origin-top-left"}
            closeOnContentClick
          >
            <ul className={"min-w-[160px]"}>
              {values.map((item, index) => (
                <li
                  className={`px-3 py-2 hover:bg-blue-400 hover:text-white cursor-pointer ${
                    item.color || ""
                  }`}
                  key={`${fieldId}_${item.key}_${index}`}
                  onClick={() => {
                    updateRuleItem({
                      ...ruleItem,
                      data: {
                        ...ruleItem.data,
                        [fieldId]: item,
                      },
                    });
                  }}
                >
                  {item.label}
                </li>
              ))}
              {isDateField && (
                <li
                  className="px-3 py-2 hover:bg-blue-400 hover:text-white cursor-pointer border-t border-gray-100"
                  key={`${fieldId}_custom_date`}
                  onClick={() => {
                    updateRuleItem({
                      ...ruleItem,
                      data: {
                        ...ruleItem.data,
                        [fieldId]: { key: "custom_date", label: t("custom date"), value: "" },
                      },
                    });
                  }}
                >
                  {t("Custom date")}
                </li>
              )}
            </ul>
          </DropdownLayout>
          {isCustomDate && (
            <div className="mt-2 w-48">
              <DatePickerField
                value={data[fieldId]?.value || ""}
                placeholder={t("Select date")}
                showClear
                onChange={(dateValue: string) => {
                  updateRuleItem({
                    ...ruleItem,
                    data: {
                      ...ruleItem.data,
                      [fieldId]: { key: "custom_date", label: t("custom date"), value: dateValue },
                    },
                  });
                }}
              />
            </div>
          )}
        </div>
      );
    }
    // Set Field "to" value: use variable picker so context variables (e.g. {{previousStepFirstResult}}) are available
    const isSetFieldTo = fieldId === "to" && data.operator === RuleOperators.SET_FIELD;
    if (isSetFieldTo) {
      return (
        <div className="px-2 min-w-[140px] flex-1 max-w-md">
          <InputWithVariablePicker
            value={data.to?.value ?? ""}
            onChange={(value: string) => {
              updateRuleItem({
                ...ruleItem,
                data: {
                  ...ruleItem.data,
                  to: { value },
                },
              });
            }}
            placeholder={t(placeholder)}
            readOnly={readonly}
            attributes={attributes}
            extraVariables={STEP_CONTEXT_VARIABLES}
          />
        </div>
      );
    }
    return (
      <div className={"px-2"}>
        <input
          readOnly={readonly}
          className={
            "w-24 px-2 block rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-0 focus:ring-inset focus:ring-blue-400"
          }
          value={data[fieldId]?.value ?? ""}
          placeholder={t(placeholder)}
          onChange={(e) => {
            updateRuleItem({
              ...ruleItem,
              data: {
                ...ruleItem.data,
                [fieldId]: {
                  value: e.target.value,
                },
              },
            });
          }}
        />
      </div>
    );
  };

  const renderFieldValuePicker = ({ data, placeholder, fromOrTo }) => {
    if (data[fromOrTo] != undefined && data.fieldId != "") {
      return <>{renderFieldType(data, fromOrTo, placeholder)}</>;
    }
  };

  const renderRuleItem = () => {
    const { data } = ruleItem;
    const placeholderTo = isFilterRule ? "is" : "to";
    return (
      <div className={"flex align-middle"}>
        {renderFieldPicker(data)}
        {renderFieldValuePicker({
          data,
          placeholder: "from",
          fromOrTo: "from",
        })}
        {renderFieldValuePicker({
          data,
          placeholder: placeholderTo,
          fromOrTo: "to",
        })}
      </div>
    );
  };

  const isJiraOrJsmItem = (item: { data?: { type?: string } }) =>
    item.data?.type === "JIRA" || item.data?.type === "JSM";

  const buildRuleItems = () => {
    if (itemType == RuleItemTypes.EVENT) {
      return isJiraContext() ? events : events.filter((e) => !isJiraOrJsmItem(e));
    }
    if (itemType == RuleItemTypes.ACTION) {
      return isJiraContext() ? actions : actions.filter((a) => !isJiraOrJsmItem(a));
    }
    if (itemType == RuleItemTypes.FILTER) {
      return filters.filter((filter) => filter.data.inputType == inputType);
    }
    return [];
  };

  const ruleItems: any = buildRuleItems();

  const renderHubspotWebhookEvent = (ruleItem: any) => {
    if (readonly) {
      return null;
    }
    if (ruleItem.value == "hubspot_incoming_webhook") {
      const statusLabel = ruleItem.data.webhookConnected ? "Connected" : "Not Connected";
      const ruleId = rule.get("id") || "";
      return (
        <div>
          <div className={"flex space-x-2"}>
            <div className={"font-semibold"}>{t("Webhook URL")}:</div>
            <div>{`${getApiBaseUrl()}/api/webhooks/${ruleId}`}</div>
          </div>
          <div className={"flex space-x-2"}>
            <div className={"font-semibold"}>{t("Status")}:</div>
            <div>{statusLabel}</div>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderHubspotWebhookAction = (ruleItem) => {
    if (readonly || isNewRule) {
      return null;
    }
    if (ruleItem.value == "convert_hubspot_contact_to_sale") {
      const webhookPayload =
        rule.getIn(["startEvent", "data", "webhookPayload", "properties"]) || fromJS({});

      const webhookMappings =
        fromJS(ruleItem).getIn(["data", "mappings"]) ||
        fromJS({ SALE: [], CONTACT: [], COMPANY: [] });

      const hubspotFields = Object.keys(webhookPayload.toJS()).map((fieldId) => ({
        label: fieldId,
        value: fieldId,
      }));
      return (
        <RuleFieldMapper
          readonly={true}
          fieldMappings={webhookMappings.toJS()}
          sourceOptions={hubspotFields}
          onChangeMappings={(mappings) => {
            updateRuleItem({
              ...ruleItem,
              data: {
                ...ruleItem.data,
                mappings: mappings,
              },
            });
          }}
        />
      );
    }
  };

  const [dropdownSearch, setDropdownSearch] = useState("");

  const eventActivator = (
    <div
      className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 bg-white transition-all w-full"
      data-rule-item-dropdown
    >
      <RuleItemAppIcon data={ruleItem.data} itemType={itemType} />
      <span className="flex-1 text-sm font-medium text-gray-900 truncate">
        {t(ruleItem.label || ruleItem.data?.title || ruleItem.data?.description || placeholder)}
      </span>
      <svg
        className="w-4 h-4 text-gray-400 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );

  if (readonly) {
    const label = t(
      ruleItem.description || ruleItem.data?.title || ruleItem.data?.description || placeholder
    );
    const data = ruleItem.data || {};
    const isSetField = data.operator === RuleOperators.SET_FIELD && data.fieldId;

    // Resolve field name for SET_FIELD
    let fieldLabel = "";
    if (isSetField && fields) {
      const matchedField = fields.find((f: any) => f.get("id") === data.fieldId);
      fieldLabel = matchedField ? matchedField.get("label") : data.fieldId;
    }

    // Resolve "to" value for any event/action type
    let toLabel = "";
    const toData = data.to;
    if (toData?.label) {
      toLabel = toData.label;
    } else if (toData?.value) {
      toLabel = toData.value;
    }

    // For events: also check data.fieldValue as some events store the value differently
    if (!toLabel && data.fieldValue) {
      toLabel = data.fieldValue;
    }

    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <RuleItemAppIcon data={data} itemType={itemType} />
        <span className="text-sm text-gray-700 truncate shrink-0">{label}</span>
        {isSetField && fieldLabel && (
          <>
            <span className="text-xs text-gray-400 shrink-0">{t("in")}</span>
            <span className="text-sm text-blue-600 font-medium truncate">{fieldLabel}</span>
          </>
        )}
        {toLabel && (
          <>
            <span className="text-xs text-gray-400 shrink-0">{isSetField ? "→" : t("to")}</span>
            <span className="text-sm text-blue-600 font-medium truncate">{toLabel}</span>
          </>
        )}
      </div>
    );
  }

  // lockedSelection: show static label (no dropdown) but keep rendering sub-editors
  if (lockedSelection && ruleItem.value) {
    const isAIAction = ruleItem.data?.type === "AI_FIELD";
    return (
      <div>
        <div className="flex flex-col gap-3 w-full">
          {/* AI actions: skip redundant static label (header already shows the name) */}
          {!isAIAction && (
            <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 w-full">
              <RuleItemAppIcon data={ruleItem.data} itemType={itemType} />
              <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                {t(
                  ruleItem.label ||
                    ruleItem.data?.title ||
                    ruleItem.data?.description ||
                    placeholder
                )}
              </span>
            </div>
          )}
          {renderRuleItem()}
        </div>
      </div>
    );
  }

  const SET_FIELD_INPUT_TYPES = ["SALE", "CONTACT", "COMPANY", "PRODUCT"];

  const getRuleItems = () => {
    let items = ruleItems.filter((item) => !isManagedByApp(item.value));
    // For actions, only show those supported by the current input type (e.g. SALE, CONTACT)
    // AI_FIELD and SET_FIELD actions are shown for multiple record types
    if (itemType === RuleItemTypes.ACTION) {
      items = items.filter((item) => {
        if (item.data.inputType === inputType) return true;
        if (item.data.type === "AI_FIELD") return true;
        const isSetField = item.data.operator === RuleOperators.SET_FIELD;
        if (isSetField && SET_FIELD_INPUT_TYPES.includes(inputType)) return true;
        return false;
      });
    }
    return items;
  };

  const filteredRuleItems = getRuleItems().filter((item) => {
    if (!dropdownSearch) return true;
    const q = dropdownSearch.toLowerCase();
    return (
      item.label?.toLowerCase().includes(q) || item.data?.description?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="flex flex-col gap-3 w-full">
        <div className="w-full">
          <DropdownLayout
            activator={eventActivator}
            fullWidth={true}
            positionClassName={"left-0 origin-top-left"}
            closeOnContentClick
          >
            <div className="py-1.5">
              {/* Search input */}
              <div className="px-2.5 pb-2">
                <div className="relative">
                  <svg
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white placeholder:text-gray-400"
                    placeholder={t("Search...")}
                    value={dropdownSearch}
                    onChange={(e) => {
                      e.stopPropagation();
                      setDropdownSearch(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
              </div>
              {/* Items */}
              <ul className="overflow-y-auto">
                {filteredRuleItems.map((item, index) => {
                  const isCurrentItem = ruleItem.value === item.value;
                  return (
                    <li
                      className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isCurrentItem ? "bg-gray-50" : "hover:bg-gray-50"}`}
                      key={`${itemType}_${item.value}_${index}`}
                      onClick={() => {
                        if (readonly) return;
                        setDropdownSearch("");
                        if (item.data?.type === "EMAIL" && currentUserEmail) {
                          const itemWithDefaultSender = {
                            ...item,
                            data: { ...item.data, sender: currentUserEmail },
                          };
                          updateRuleItem(itemWithDefaultSender);
                        } else if (item.data?.type === "AI_FIELD") {
                          const itemWithDynamicType = {
                            ...item,
                            data: { ...item.data, inputType, outputType: inputType },
                          };
                          updateRuleItem(itemWithDynamicType);
                        } else if (item.data?.operator === RuleOperators.SET_FIELD) {
                          const itemWithDynamicType = {
                            ...item,
                            data: { ...item.data, inputType, outputType: inputType },
                          };
                          updateRuleItem(itemWithDynamicType);
                        } else {
                          updateRuleItem(item);
                        }
                      }}
                    >
                      <RuleItemAppIcon data={item.data} itemType={itemType} />
                      <span className="flex-1 text-sm text-gray-900 truncate">
                        {t(item.data?.description || item.label)}
                      </span>
                      {isCurrentItem && (
                        <svg
                          className="w-5 h-5 text-blue-600 shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </li>
                  );
                })}
                {filteredRuleItems.length === 0 && (
                  <li className="px-3 py-4 text-sm text-gray-400 text-center">
                    {t("No results found")}
                  </li>
                )}
              </ul>
            </div>
          </DropdownLayout>
          {renderHubspotWebhookEvent(ruleItem)}
          {renderHubspotWebhookAction(ruleItem)}
        </div>
        {renderRuleItem()}
      </div>
    </div>
  );
}

export default RuleItemBuilder;
